import React, { useEffect, useState } from 'react';
import { Flame, Zap, Target, Award, TrendingUp } from 'lucide-react';
import { Progress } from './ui/progress';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BADGES = {
  first_lesson: { icon: '🌟', name: 'İlk Adım', desc: 'İlk dersini tamamladın' },
  ten_lessons: { icon: '🌟', name: '10 Ders', desc: '10 ders tamamladın' },
  week_streak: { icon: '🔥', name: 'Haftalık Seri', desc: '7 gün üst üste çalıştın' },
  century: { icon: '💯', name: 'Yüzcü', desc: '100 Mil kazandın' },
  daily_goal: { icon: '🎯', name: 'Hedef Tamam', desc: 'Günlük hedefini tamamladın' },
};

export default function GamificationBar({ compact = false }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/gamification/program-stats`, { withCredentials: true });
      setStats(res.data);
    } catch (error) {
      console.error('Stats yüklenemedi:', error);
      setStats({
        xp: 0,
        level: 1,
        streak: 0,
        daily_goal_minutes: 10,
        today_minutes: 0,
        badges: []
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) {
    return <div className="h-16 bg-white/50 animate-pulse rounded-xl"></div>;
  }

  const dailyProgress = Math.min(((stats.today_minutes || 0) / (stats.daily_goal_minutes || 10)) * 100, 100);
  const xpProgress = ((stats.xp || 0) % 100);
  const badges = Array.isArray(stats.badges) ? stats.badges : [];

  if (compact) {
    return (
      <div className="flex items-center gap-4 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-xl border border-gray-100">
        {/* Streak */}
        <div className="flex items-center gap-1.5">
          <Flame className={`h-5 w-5 ${stats.streak > 0 ? 'text-orange-500' : 'text-gray-300'}`} />
          <span className="font-bold text-gray-800">{stats.streak}</span>
        </div>
        
        {/* Mil */}
        <div className="flex items-center gap-1.5">
          <Zap className="h-5 w-5 text-yellow-500" />
          <span className="font-bold text-gray-800">{stats.xp}</span>
        </div>
        
        {/* Level */}
        <div className="flex items-center gap-1.5 bg-indigo-100 px-2 py-0.5 rounded-full">
          <TrendingUp className="h-4 w-4 text-indigo-600" />
          <span className="font-bold text-indigo-600 text-sm">Lv.{stats.level}</span>
        </div>
        
        {/* Daily Goal */}
        <div className="flex-1 max-w-[120px]">
          <div className="flex items-center gap-1.5 mb-1">
            <Target className="h-4 w-4 text-emerald-500" />
            <span className="text-xs text-gray-500">{stats.today_minutes}/{stats.daily_goal_minutes}dk</span>
          </div>
          <Progress value={dailyProgress} className="h-1.5" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
      <div className="grid grid-cols-4 gap-4">
        {/* Streak */}
        <div className="text-center p-3 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl">
          <Flame className={`h-8 w-8 mx-auto mb-1 ${stats.streak > 0 ? 'text-orange-500' : 'text-gray-300'}`} />
          <p className="text-2xl font-bold text-gray-800">{stats.streak}</p>
          <p className="text-xs text-gray-500">Gün Serisi</p>
        </div>
        
        {/* Mil {/* XP & Level */} Level */}
        <div className="text-center p-3 bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl">
          <Zap className="h-8 w-8 mx-auto mb-1 text-yellow-500" />
          <p className="text-2xl font-bold text-gray-800">{stats.xp}</p>
          <p className="text-xs text-gray-500">Mil (Lv.{stats.level})</p>
          <Progress value={xpProgress} className="h-1 mt-2" />
        </div>
        
        {/* Daily Goal */}
        <div className="text-center p-3 bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl">
          <Target className="h-8 w-8 mx-auto mb-1 text-emerald-500" />
          <p className="text-2xl font-bold text-gray-800">{stats.today_minutes}<span className="text-sm">/{stats.daily_goal_minutes}dk</span></p>
          <p className="text-xs text-gray-500">Günlük Hedef</p>
          <Progress value={dailyProgress} className="h-1 mt-2" />
        </div>
        
        {/* Badges */}
        <div className="text-center p-3 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl">
          <Award className="h-8 w-8 mx-auto mb-1 text-purple-500" />
          <p className="text-2xl font-bold text-gray-800">{badges.length}</p>
          <p className="text-xs text-gray-500">Rozet</p>
          <div className="flex justify-center gap-1 mt-2">
            {badges.slice(0, 3).map((badge, i) => (
              <span key={i} className="text-lg" title={BADGES[badge]?.name}>
                {BADGES[badge]?.icon || '⭐'}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
